import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

const RANGES = [
  { key: 'all', label: 'Lifetime',  days: null },
  { key: '6m',  label: '6 months', days: 183  },
  { key: '30d', label: '30 days',  days: 30   },
  { key: '7d',  label: '7 days',   days: 7    },
];

const CLTV_COLORS  = { 0: '#7a8499', 1: '#E21351', 2: '#f0a500', 3: '#d4a843', 4: '#00BD71' };
const CLTV_LABELS  = { 0: '≤ €10', 1: '€10–50', 2: '€50–200', 3: '€200–700', 4: '> €700' };

function cutoffIso(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function isoDate(raw) {
  return (raw || '').slice(0, 10);
}

function fmtDay(iso, mode = 'day') {
  if (!iso) return '';
  const date = new Date(iso.slice(0, 10) + 'T12:00:00');
  const yr = `'${String(date.getFullYear()).slice(2)}`;
  if (mode === 'month') return `${date.toLocaleDateString('en-GB', { month: 'short' })} ${yr}`;
  if (mode === 'dayYear') return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${yr}`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function pobaColor(poba) {
  if (poba >= 0.7) return '#00BD71';
  if (poba >= 0.4) return '#FFD22F';
  return '#E21351';
}

function buildTicks(pts, rangeDays) {
  if (!pts.length) return [];
  if (rangeDays === 7)   return pts.map((p) => p.day);
  if (rangeDays === 30)  return pts.filter((_, i) => i % 5 === 0).map((p) => p.day);
  if (rangeDays === 183) return pts.filter((_, i) => i % 14 === 0).map((p) => p.day);
  // Lifetime: collect month boundaries, then thin based on total count
  const months = [];
  let lastMonth = -1;
  pts.forEach((p) => {
    if (p.month !== lastMonth) { months.push(p.day); lastMonth = p.month; }
  });
  const step = months.length > 18 ? 3 : months.length > 9 ? 2 : 1;
  return months.filter((_, i) => i % step === 0);
}

function RangeToggle({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderColor: active ? '#4fc3f7' : '#2a3042',
        color: active ? '#4fc3f7' : '#7a8499',
        backgroundColor: active ? '#4fc3f718' : 'transparent',
      }}
      className="px-2.5 py-1 text-[11px] cursor-pointer rounded border select-none"
    >
      {children}
    </button>
  );
}

// ─── PoBA chart ───────────────────────────────────────────────────────────────

function PobaTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0d0f12] border border-[#2a3042] rounded p-2 text-[11px]">
      <p className="font-bold text-[#d8dde8] mb-1">{row.day}</p>
      <p style={{ color: pobaColor(row.poba) }}>PoBA: {(row.poba * 100).toFixed(1)}%</p>
    </div>
  );
}

function ColoredDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={2.5} fill={pobaColor(payload.poba)} />;
}

// ─── CLTV chart ───────────────────────────────────────────────────────────────

function CltvTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  if (row.cltv == null) return null;
  const color = CLTV_COLORS[row.cltv] || '#7a8499';
  return (
    <div className="bg-[#0d0f12] border border-[#2a3042] rounded p-2 text-[11px]">
      <p className="font-bold text-[#d8dde8] mb-1">{row.day}</p>
      <p style={{ color }}>CLTV: L{row.cltv} — {CLTV_LABELS[row.cltv] || '?'}</p>
    </div>
  );
}

// Dot colored by the bucket value.
function CltvDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || payload.cltv == null) return null;
  return <circle cx={cx} cy={cy} r={3} fill={CLTV_COLORS[payload.cltv] || '#7a8499'} />;
}

function cltvTickFormatter(v) {
  return `L${v}`;
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function PobaChart({ rows }) {
  const [range, setRange] = useState('all');
  const cfg = RANGES.find((r) => r.key === range);

  const filtered = useMemo(() => {
    const all = rows || [];
    if (!cfg || cfg.days === null) return all;
    const cut = cutoffIso(cfg.days);
    return all.filter((r) => isoDate(r.day) >= cut);
  }, [rows, cfg]);

  const { pobaData, cltvData, xTicks } = useMemo(() => {
    const isLifetime = !cfg || cfg.days === null;
    const mode = isLifetime ? 'month' : cfg.days === 183 ? 'dayYear' : 'day';
    const pts = filtered.map((r) => ({
      day: fmtDay(r.day, mode),
      month: new Date(isoDate(r.day) + 'T12:00:00').getMonth(),
      poba: r.poba ?? null,
      cltv: r.cltv_label ?? null,
    }));
    const ticks = buildTicks(pts, cfg?.days ?? null);
    // Only include points that have the relevant value (skip nulls in each series).
    const pobaData = pts.filter((p) => p.poba != null);
    const cltvData = pts.filter((p) => p.cltv != null);
    return { pobaData, cltvData, xTicks: ticks };
  }, [filtered, cfg]);

  const hasPoba = pobaData.length > 0;
  const hasCltv = cltvData.length > 0;

  if (!(rows || []).length) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <p className="text-[13px] text-ink-dim text-center">No prediction history available for this player.</p>
      </div>
    );
  }

  const empty = !hasPoba && !hasCltv;

  return (
    <div className="bg-[#13161b] border border-[#2a3042] rounded p-4 flex flex-col gap-6">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-bold text-[#d8dde8]">Prediction history</p>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <RangeToggle key={r.key} active={range === r.key} onClick={() => setRange(r.key)}>
              {r.label}
            </RangeToggle>
          ))}
        </div>
      </div>

      {empty ? (
        <div className="flex justify-center items-center h-[180px]">
          <p className="text-[13px] text-[#7a8499]">No data in this time range.</p>
        </div>
      ) : (
        <>
          {/* ── PoBA panel ── */}
          {hasPoba && (
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <p className="text-[11px] font-bold text-[#7a8499] uppercase tracking-wide">Probability of Being Alive (PoBA)</p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-1.5">
                    <div style={{ width: 20, height: 0, borderTop: '2px dashed #00BD71' }} />
                    <span className="text-[10px] text-[#7a8499]">70% safe threshold</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div style={{ width: 20, height: 0, borderTop: '2px dashed #FFD22F' }} />
                    <span className="text-[10px] text-[#7a8499]">40% high risk</span>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={pobaData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid stroke="#1f2430" vertical={false} />
                  <XAxis dataKey="day" ticks={xTicks} interval={0}
                    tick={{ fontSize: 9, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }} />
                  <YAxis domain={[0, 1]} ticks={[0, 0.2, 0.4, 0.6, 0.7, 0.8, 1.0]}
                    tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                    tick={{ fontSize: 9, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }} width={40} />
                  <Tooltip content={<PobaTooltip />} />
                  <ReferenceLine y={0.7} stroke="#00BD71" strokeDasharray="4 3" strokeWidth={1.5} />
                  <ReferenceLine y={0.4} stroke="#FFD22F" strokeDasharray="4 3" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="poba" stroke="#4fc3f7" strokeWidth={2}
                    dot={pobaData.length <= 60 ? <ColoredDot /> : false}
                    activeDot={{ r: 4 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── CLTV panel ── */}
          {hasCltv && (
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <p className="text-[11px] font-bold text-[#7a8499] uppercase tracking-wide">CLTV bucket</p>
                <div className="flex gap-2 flex-wrap">
                  {[0, 1, 2, 3, 4].map((b) => (
                    <div key={b} className="flex items-center gap-1">
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: CLTV_COLORS[b] }} />
                      <span className="text-[10px] text-[#7a8499]">L{b} {CLTV_LABELS[b]}</span>
                    </div>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={cltvData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid stroke="#1f2430" vertical={false} />
                  <XAxis dataKey="day" ticks={xTicks} interval={0}
                    tick={{ fontSize: 9, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }} />
                  <YAxis domain={[-0.5, 4.5]} ticks={[0, 1, 2, 3, 4]}
                    tickFormatter={cltvTickFormatter}
                    tick={{ fontSize: 9, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }} width={28} />
                  <Tooltip content={<CltvTooltip />} />
                  <Line type="stepAfter" dataKey="cltv" stroke="#d4a843" strokeWidth={2}
                    dot={cltvData.length <= 60 ? <CltvDot /> : false}
                    activeDot={{ r: 4 }} isAnimationActive={false} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      <p className="text-[10px] text-[#5a6477] italic">
        PoBA = Probability of Being Alive. 100% = certain to stay, 0% = churned. CLTV bucket predicts lifetime value tier.
      </p>
    </div>
  );
}
